import { useCallback, useEffect, useRef, useState } from 'react';
import { injectTSDI } from '../../../../core/tsdi';
import { Api } from '../../../../core/api';
import {
    ActivityDto,
    ActivityType
} from '../../../../core/api/FitnessActivityClient';

const PAGE_SIZE = 50;

export interface UseWorkoutListResult {
    workouts: ActivityDto[];
    isLoading: boolean;
    error: string | null;
    hasMore: boolean;
    loadMore: () => void;
    reload: () => void;
}

export function useWorkoutList(userId: string): UseWorkoutListResult {
    const [workouts, setWorkouts] = useState<ActivityDto[]>([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestSeqRef = useRef(0);

    const fetchPage = useCallback(
        async (pageToLoad: number, append: boolean) => {
            const api = injectTSDI(Api);
            const seq = ++requestSeqRef.current;
            setIsLoading(true);
            setError(null);
            try {
                const response =
                    await api.fitnessActivityClient.admin.activitySupportControllerGetActivities(
                        {
                            userId,
                            page: pageToLoad,
                            size: PAGE_SIZE,
                            sort: '-timestamp'
                        }
                    );
                if (seq !== requestSeqRef.current) return;

                const content = response.data.content ?? [];
                const workoutsOnly = content.filter(
                    (a) => a.type === ActivityType.WORKOUT
                );

                setWorkouts((prev) =>
                    append ? [...prev, ...workoutsOnly] : workoutsOnly
                );
                const totalPages = response.data.totalPages ?? 0;
                setHasMore(pageToLoad + 1 < totalPages);
                setPage(pageToLoad);
            } catch (e: unknown) {
                if (seq !== requestSeqRef.current) return;
                const msg =
                    e instanceof Error ? e.message : 'Failed to load workouts';
                setError(msg);
            } finally {
                if (seq === requestSeqRef.current) setIsLoading(false);
            }
        },
        [userId]
    );

    useEffect(() => {
        setWorkouts([]);
        setPage(0);
        setHasMore(true);
        fetchPage(0, false);
    }, [fetchPage]);

    const loadMore = useCallback(() => {
        if (isLoading || !hasMore) return;
        fetchPage(page + 1, true);
    }, [fetchPage, isLoading, hasMore, page]);

    const reload = useCallback(() => {
        setWorkouts([]);
        setPage(0);
        setHasMore(true);
        fetchPage(0, false);
    }, [fetchPage]);

    return { workouts, isLoading, error, hasMore, loadMore, reload };
}
